import { GradientRing } from '@/components/feedback/gradient-ring';
import { open, useSidebarActive } from '@/features/sidebar';
import { useIsFullScreenAssistantPage } from '@/features/copilot/hooks';
import { useCopilotSessionContext } from '@/features/copilot/components/session-provider';
import { GlobalCSSVariable, useThemeContext } from '@/context/theme';
import { Button, SkeletonShape } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { Suggestions, useSuggestions } from '../suggestions';
import { sentryCaptureException } from '@/utils/sentry';
import { useAnalytics } from '@/services/analytics';
import { useLocalStorage } from '@/hooks/browser/use-local-storage';
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import * as styles from './index.css';

export const ASSISTANT_FOOTER_HEIGHT = '45px';

// Large sparkle (filled) — center ~13.29, 12
const SPARKLE_LARGE_PATH =
  'M15.987 9.36 23 12l-7.013 2.639L13.29 21.5l-2.699-6.861L3.58 12l7.012-2.64L13.29 2.5l2.697 6.86Z';
// Bottom-left small star — center ~5.85, 17.78
const SPARKLE_SMALL_BOTTOM_PATH = 'M5.189 19.068H3v-1.285h2.19V15.64h1.313v2.142h2.189v1.285h-2.19v2.142H5.19v-2.142Z';
// Top-left small star — center ~5.85, 5.575
const SPARKLE_SMALL_TOP_PATH = 'M5.19 6.217H3V4.932h2.19V2.79h1.313v2.142h2.189v1.285h-2.19V8.36H5.19V6.217Z';

const TRANSITION = { duration: 0.2, ease: [0.2, 0, 0.38, 0.9] } as const;

function GradientSparkleIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <defs>
        <linearGradient id="assistant-footer-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" style={{ stopColor: themeVars.palette['purple-400'] }} />
          <stop offset="100%" style={{ stopColor: themeVars.palette['orange-300'] }} />
        </linearGradient>
      </defs>
      <path d={SPARKLE_LARGE_PATH} fill="url(#assistant-footer-gradient)" />
      <path d={SPARKLE_SMALL_TOP_PATH} fill="url(#assistant-footer-gradient)" />
      <path d={SPARKLE_SMALL_BOTTOM_PATH} fill="url(#assistant-footer-gradient)" />
    </svg>
  );
}

function AskAssistantButton({ onClick }: { onClick: () => void }) {
  return (
    <GradientRing alwaysVisible ringSize="2px" radius={4}>
      <Button variant="secondary" size="sm" style={{ border: 'none' }} onClick={onClick}>
        <GradientSparkleIcon />
        Ask Agent
      </Button>
    </GradientRing>
  );
}

export function AssistantFooter() {
  const isCopilotSidebarOpen = useSidebarActive('copilot');
  const isFullScreenAssistantPage = useIsFullScreenAssistantPage();
  const session = useCopilotSessionContext();
  const suggestionState = useSuggestions(session);
  const { track } = useAnalytics();
  const themeContext = useThemeContext();
  const [isCollapsed, setIsCollapsed] = useLocalStorage('assistant-footer-collapsed', false);
  const [showCollapsedUI, setShowCollapsedUI] = useState(isCollapsed);
  const [showExpandedUI, setShowExpandedUI] = useState(!isCollapsed);
  const isCollapsedRef = useRef(isCollapsed);

  const isHidden = isCopilotSidebarOpen || isFullScreenAssistantPage;

  useEffect(() => {
    const isVisible = !isHidden && showExpandedUI;
    themeContext.setVariable(GlobalCSSVariable.AssistantFooterHeight, isVisible ? ASSISTANT_FOOTER_HEIGHT : '0px');

    return () => {
      themeContext.setVariable(GlobalCSSVariable.AssistantFooterHeight, '0px');
    };
  }, [isHidden, showExpandedUI, themeContext]);

  if (isHidden) {
    return null;
  }

  const handleSuggestionClick = async (message: string, index: number) => {
    try {
      open('copilot');
      await session.sendMessage(message);
      track('aiAssistant', 'suggestionClicked', {
        Index: index,
        'Click Location': 'footer',
      });
    } catch (error) {
      sentryCaptureException(error);
    }
  };

  const handleAskAssistant = () => {
    open('copilot');
    track('aiAssistant', 'opened', { 'Click Location': 'footer' });
  };

  const handleToggleCollapse = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    isCollapsedRef.current = next;
    track('aiAssistant', next ? 'footerCollapsed' : 'footerExpanded', {});
    if (next) {
      // Collapsing: shrink height, then show collapsed UI on animation complete
      if (showExpandedUI) {
        setShowExpandedUI(false);
      } else {
        // Expanded UI already hidden (rapid toggle) — show collapsed directly
        setShowCollapsedUI(true);
      }
    } else {
      // Expanding: hide collapsed UI, then open height on exit complete
      if (showCollapsedUI) {
        setShowCollapsedUI(false);
      } else {
        // Collapsed UI hasn't appeared yet (rapid toggle) — show expanded directly
        setShowExpandedUI(true);
      }
    }
  };

  return (
    <>
      {/* Expanded bar with animated height */}
      <motion.div
        className={styles.heightWrapper}
        initial={false}
        animate={{ height: showExpandedUI ? 'auto' : 0 }}
        transition={TRANSITION}
        onAnimationComplete={() => {
          if (isCollapsedRef.current) {
            setShowCollapsedUI(true);
          }
        }}
      >
        <div className={styles.footer}>
          <div className={styles.suggestionsContainer}>
            <AnimatePresence mode="popLayout">
              {!isCollapsed && suggestionState.isVisible
                ? suggestionState.suggestions.map((suggestion, index) => (
                    <motion.div
                      key={suggestion.label}
                      layout
                      initial={{ opacity: 0, scale: 0.8, filter: 'blur(4px)' }}
                      animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                      exit={{ opacity: 0, scale: 0.8, filter: 'blur(4px)' }}
                      transition={{ ...TRANSITION, delay: index * 0.05 }}
                    >
                      <Suggestions
                        suggestions={[suggestion]}
                        onSuggestionClick={(message) => handleSuggestionClick(message, index + 1)}
                      />
                    </motion.div>
                  ))
                : !isCollapsed
                ? [140, 120, 160].map((width, index) => (
                    <motion.div
                      key={`skeleton-${index}`}
                      layout
                      initial={{ opacity: 0, scale: 0.8, filter: 'blur(4px)' }}
                      animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                      exit={{ opacity: 0, scale: 0.8, filter: 'blur(4px)' }}
                      transition={{ ...TRANSITION, delay: index * 0.05 }}
                    >
                      <SkeletonShape style={{ width, height: 29, borderRadius: themeVars.radius['4'] }} />
                    </motion.div>
                  ))
                : null}
            </AnimatePresence>
          </div>
          <div className={styles.actionsContainer}>
            <AskAssistantButton onClick={handleAskAssistant} />
            <Button variant="subtle" size="sm" icon="chevron-down" isIconOnly onClick={handleToggleCollapse} />
          </div>
        </div>
      </motion.div>

      {/* Fixed collapsed button — only shows after height animation completes */}
      <AnimatePresence
        onExitComplete={() => {
          if (!isCollapsedRef.current) {
            setShowExpandedUI(true);
          }
        }}
      >
        {showCollapsedUI ? (
          <motion.div
            className={styles.collapsedContainer}
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            exit={{ y: 20 }}
            transition={TRANSITION}
          >
            <AskAssistantButton onClick={handleAskAssistant} />
            <Button variant="subtle" size="sm" icon="chevron-up" isIconOnly onClick={handleToggleCollapse} />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
}
